import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, MessageSquare } from "lucide-react";
import Avatar from "../common/Avatar";
import {
  playerInitials,
  playerMetaParts,
  playerAgentLine,
} from "./playerRecord";

// A6·b — one proposal card in the club's delivery view (A2).
//
// Player identity (photo, name, position · age · market value · contract end) is
// read straight off the hydrated record the endpoint delivers — never
// free-typed (Annex Z.7). The only free text is the delivering agent's note; the
// "Player's agent" line comes from the player's own agent record.
//
// Actions: "View profile" opens the EXISTING player profile (A13, same tab so
// browser-back returns here — "back leads to the proposals"); "Contact agent"
// opens the A8 club↔player's-agent thread.
//
// Mobile (M·A2): the meta segments wrap to their own lines, never shrink; the
// two actions stack full-width ("Contact agent" full width), side-by-side from
// sm.
const ProposalCard = ({ proposal, onContactAgent, contacting }) => {
  const navigate = useNavigate();
  const player = proposal?.player || {};
  const meta = playerMetaParts(player);
  const agentLine = playerAgentLine(proposal?.player_agent);

  const viewProfile = () => {
    if (player.id) navigate(`/search/player/${player.id}`);
  };

  return (
    <div className="bg-white border border-[#D6DBE3] rounded-xl p-4 sm:p-5 flex flex-col gap-3.5">
      {/* Player head — record data only */}
      <div className="flex items-start gap-3">
        <Avatar
          src={player.image}
          fallbackText={player.name || "?"}
          alt={player.name || ""}
          size={48}
          className="flex-none"
        />
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[15px] font-bold text-[#111827] break-words">
            {player.name || playerInitials(player.name)}
          </span>
          {meta.length > 0 && (
            <span className="text-[12.5px] text-[#4B5563] flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5">
              {meta.map((part, i) => (
                <React.Fragment key={part + i}>
                  {i > 0 && <span className="text-[#D1D5DB]">·</span>}
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </span>
          )}
        </div>
      </div>

      {/* The delivering agent's note (the only free text on the card) */}
      {proposal?.note && (
        <p className="text-[13px] text-[#374151] leading-relaxed bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 break-words">
          {proposal.note}
        </p>
      )}

      {/* Player's own agent — from the record */}
      {agentLine && (
        <div className="text-[12px] text-gray-500">
          <span className="font-semibold text-gray-400">Player&apos;s agent: </span>
          <span className="text-[#374151]">{agentLine}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button
          type="button"
          onClick={viewProfile}
          className="inline-flex items-center justify-center gap-1.5 flex-1 border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] font-semibold text-[#111827] hover:bg-gray-50"
        >
          View profile
          <ArrowUpRight className="w-4 h-4 text-gray-400" />
        </button>
        <button
          type="button"
          onClick={() => onContactAgent?.(proposal)}
          disabled={contacting}
          className="inline-flex items-center justify-center gap-1.5 flex-1 bg-[#2283FF] text-white rounded-lg px-3.5 py-2.5 text-[13px] font-semibold hover:bg-[#1b6fd8] disabled:opacity-60"
        >
          <MessageSquare className="w-4 h-4" />
          {contacting ? "Opening…" : "Contact agent"}
        </button>
      </div>
    </div>
  );
};

export default ProposalCard;
